//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { Change } from "../../api/changelog/v1/ichangelog";
import { DeploymentNotificationRule, DeploymentNotificationSummary, ListAllDeploymentsRequest } from "../../api/data/v1/idata";
import { NotificationSeverity } from "../../api/lib";
import { ListAllOrganizationsRequest, OrganizationNotificationRule, OrganizationNotificationSummary } from "../../api/resourcemanager/v1/iresourcemanager";
import { NotificationSeverity as OrganizationNotificationSeverity } from "../../api/resourcemanager/v1/resourcemanager";
import { NotificationData } from "../notifications/NotificationFields";

// A rule is always an operator-written message plus a scope. Notifications whose condition
// is not a filter (version below minimum, read-only, VST, delinquent payer, ...) stay as
// built-in inspectors configured from the config repo and only appear in the inventory.

// NotificationRuleTarget selects what the notification is attached to.
// Deployment rules are stored by datad, organization rules by rmd; both are reconciled by the inspectors.
export type NotificationRuleTarget = "deployment" | "organization";

export const targetLabels: Record<NotificationRuleTarget, string> = {
  deployment: "Deployments",
  organization: "Organizations",
};

// Header of the modal showing a rule's scope filters.
export const filtersTitle: Record<NotificationRuleTarget, string> = {
  deployment: "Deployment filters",
  organization: "Organization filters",
};

// Format of a rule's notification ID, enforced by datad and rmd (notificationIDPattern).
export const ruleNotificationIdFormat = {
  pattern: /^[A-Z0-9_]+$/,
  hint: "Uppercase letters, digits and underscores only (^[A-Z0-9_]+$)",
  example: "PLANNED_MAINTENANCE_2026_10",
};

// Page size used to list rules per target; matches the cap the deployment/organization inspectors load.
export const maxRulesPerTarget = 250;

// UI view of a rule from either service.
export interface NotificationRule {
  id: string;
  url: string;
  target: NotificationRuleTarget;
  notification: NotificationData;
  // Scope of a deployment rule, evaluated by datad (same filter as the deployment updater).
  deployment_filter?: ListAllDeploymentsRequest;
  // Scope of an organization rule, evaluated by rmd.
  organization_filter?: ListAllOrganizationsRequest;
  enabled: boolean;
  created_at?: Date;
  created_by_id: string;
  updated_at?: Date;
  updated_by_id?: string;
  // Reported by the inspector.
  matched: number;
  last_reconciled_at?: Date;
}

// toDate converts a timestamp as it arrives in JSON: an RFC 3339 string from the API, or
// {seconds, nanos} from a changelog snapshot (encoding/json of the proto message).
export const toDate = (t: unknown): Date | undefined => {
  if (!t) return undefined;
  if (typeof t === "object" && !(t instanceof Date) && "seconds" in (t as object)) {
    const { seconds, nanos } = t as { seconds?: number; nanos?: number };
    return new Date((seconds || 0) * 1000 + Math.floor((nanos || 0) / 1e6));
  }
  return new Date(t as string);
};

// toSeverity normalizes a severity as it arrives in JSON: the gateway renders enums by name
// ("NOTIFICATION_SEVERITY_WARNING"), changelog snapshots use the number.
// Both services define the same NotificationSeverity enum.
export const toSeverity = (s: unknown): NotificationSeverity => {
  if (typeof s === "string") {
    const v = ((NotificationSeverity as unknown) as Record<string, number>)[s];
    return (typeof v === "number" ? v : Number(s) || 0) as NotificationSeverity;
  }
  return ((s as number) || 0) as NotificationSeverity;
};

const notificationOf = (r: DeploymentNotificationRule | OrganizationNotificationRule): NotificationData => ({
  notificationId: r.notification_id || "",
  notificationText: r.notification_text || "",
  severity: toSeverity(r.severity),
  expiresAt: toDate(r.expires_at),
});

const commonOf = (r: DeploymentNotificationRule | OrganizationNotificationRule) => ({
  id: r.id || "",
  url: r.url || "",
  notification: notificationOf(r),
  enabled: !!r.enabled,
  created_at: toDate(r.created_at),
  created_by_id: r.created_by_id || "",
  updated_at: toDate(r.updated_at),
  updated_by_id: r.updated_by_id,
  matched: r.status?.matched || 0,
  last_reconciled_at: toDate(r.status?.last_reconciled_at),
});

export const fromDeploymentRule = (r: DeploymentNotificationRule): NotificationRule => ({
  ...commonOf(r),
  target: "deployment",
  deployment_filter: r.deployment_filter || {},
});

export const fromOrganizationRule = (r: OrganizationNotificationRule): NotificationRule => ({
  ...commonOf(r),
  target: "organization",
  organization_filter: r.organization_filter || {},
});

// Only the writable fields are sent; id identifies the rule on update and is empty on create.
export const toDeploymentRule = (r: NotificationRule): DeploymentNotificationRule => ({
  id: r.id || undefined,
  notification_id: r.notification.notificationId,
  notification_text: r.notification.notificationText,
  severity: r.notification.severity,
  expires_at: r.notification.expiresAt,
  deployment_filter: r.deployment_filter || {},
  enabled: r.enabled,
});

export const toOrganizationRule = (r: NotificationRule): OrganizationNotificationRule => ({
  id: r.id || undefined,
  notification_id: r.notification.notificationId,
  notification_text: r.notification.notificationText,
  severity: (r.notification.severity as number) as OrganizationNotificationSeverity,
  expires_at: r.notification.expiresAt,
  organization_filter: r.organization_filter || {},
  enabled: r.enabled,
});

// Changelog URL prefix of the rules of each target (the rule URL is this prefix + rule ID); used to
// key the rule filter of the audit log. idashboardd builds the changelog query itself.
export const RULE_URL_PREFIX: Record<NotificationRuleTarget, string> = {
  deployment: "/Organization/_system/DeploymentNotificationRule/",
  organization: "/Organization/_system/OrganizationNotificationRule/",
};

// lastUrlSegment returns the (unescaped) last path segment of a resource URL, i.e. its ID.
export const lastUrlSegment = (url?: string): string => decodeURIComponent((url || "").split("/").pop() || "");

export type NotificationSummary = DeploymentNotificationSummary | OrganizationNotificationSummary;

export const ruleScope = (rule: NotificationRule): Record<string, any> =>
  rule.target === "organization" ? rule.organization_filter || {} : rule.deployment_filter || {};

// ActiveNotificationSource tells who owns a notification ID currently present on deployments/organizations.
export type ActiveNotificationSource = "rule" | "built-in" | "manual";

export const sourceLabels: Record<ActiveNotificationSource, string> = {
  rule: "Rule",
  "built-in": "Built-in inspector",
  manual: "Manual",
};

// ActiveNotification is one row of the inventory: a notification ID aggregated over all
// deployments and organizations that currently carry it.
export interface ActiveNotification {
  notification_id: string;
  source: ActiveNotificationSource;
  // Rule ID for "rule", inspector name for "built-in", empty for "manual".
  source_ref?: string;
  // Human readable condition, only for "built-in".
  condition?: string;
  severity: NotificationSeverity;
  // Sample of the text (manual notifications may differ per resource).
  text: string;
  // Non-deleted deployments / organizations; deleted (soft-deleted) ones carrying the notification are counted separately.
  deployments: number;
  deleted_deployments: number;
  organizations: number;
  deleted_organizations: number;
  oldest_created_at?: Date;
  newest_created_at?: Date;
}

// RuleChange mirrors changelogd's Change: a snapshot of the rule after every mutation, recorded
// with the caller and timestamp. The action (created / updated / enabled / disabled / deleted) is
// not stored; it is derived by comparing a snapshot with the previous one of the same rule.
export interface RuleChange {
  id: string;
  rule_id: string;
  target: NotificationRuleTarget;
  notification_id: string;
  created_at: Date;
  created_by_id: string;
  // True when the rule was deleted; `rule` is then the last version before deletion.
  removed: boolean;
  rule: RuleSnapshot;
  // Recorded payload of a "remove everywhere" action (only for removed_everywhere).
  payload?: unknown;
}

export type RuleSnapshot = Omit<NotificationRule, "matched" | "last_reconciled_at">;

export type RuleChangeAction = "created" | "updated" | "enabled" | "disabled" | "deleted" | "removed_everywhere";

export const actionLabels: Record<RuleChangeAction, string> = {
  created: "Created",
  updated: "Updated",
  enabled: "Enabled",
  disabled: "Disabled",
  deleted: "Deleted",
  removed_everywhere: "Removed everywhere",
};

export interface DerivedRuleChange extends RuleChange {
  action: RuleChangeAction;
  // Human readable list of changed fields, empty for created/deleted.
  summary: string[];
  previous?: RuleChange;
}

// emptySnapshot stands in for a rule whose content is not known (deleted rule without an earlier snapshot, removal entries).
const emptySnapshot = (target: NotificationRuleTarget, url: string, notificationId: string): RuleSnapshot => ({
  id: lastUrlSegment(url),
  url,
  target,
  notification: { notificationId, notificationText: "", severity: NotificationSeverity.NOTIFICATION_SEVERITY_INFO },
  enabled: false,
  created_by_id: "",
});

const toSnapshot = (rule: NotificationRule): RuleSnapshot => {
  const { matched, last_reconciled_at, ...snapshot } = rule;
  return snapshot;
};

// ruleChangeFromChange converts a changelog entry of a rule URL. The JSON is the rule after the change;
// a removed entry has no JSON, its rule is filled in from the previous snapshot by deriveRuleChanges.
export const ruleChangeFromChange = (c: Change, target: NotificationRuleTarget): RuleChange => {
  const url = c.url || "";
  let rule = emptySnapshot(target, url, "");
  if (!c.removed && c.json) {
    const parsed = JSON.parse(c.json);
    rule = toSnapshot(target === "organization" ? fromOrganizationRule(parsed) : fromDeploymentRule(parsed));
  }
  return {
    id: c.id || "",
    rule_id: lastUrlSegment(url),
    target,
    notification_id: rule.notification.notificationId,
    created_at: toDate(c.created_at) || new Date(0),
    created_by_id: c.created_by_id || "",
    removed: !!c.removed,
    rule,
  };
};

// removalChangeFromChange converts a "remove everywhere" changelog entry (URL = the notification URL,
// JSON = {notification_id, removed_from}).
export const removalChangeFromChange = (c: Change, target: NotificationRuleTarget): DerivedRuleChange => {
  const url = c.url || "";
  const notificationId = lastUrlSegment(url);
  let payload: { notification_id?: string; removed_from?: number } = {};
  try {
    payload = c.json ? JSON.parse(c.json) : {};
  } catch (e) {
    payload = {};
  }
  return {
    id: c.id || "",
    rule_id: "",
    target,
    notification_id: notificationId,
    created_at: toDate(c.created_at) || new Date(0),
    created_by_id: c.created_by_id || "",
    removed: true,
    rule: emptySnapshot(target, url, notificationId),
    payload,
    action: "removed_everywhere",
    summary: [`removed from ${payload.removed_from ?? 0} ${target}s`],
  };
};

const describe = (label: string, before: unknown, after: unknown) => `${label}: ${JSON.stringify(before ?? null)} → ${JSON.stringify(after ?? null)}`;

// deriveRuleChanges sorts snapshots newest first and computes the action and a field summary per snapshot.
// Snapshots are grouped by target and rule ID: datad and rmd allocate rule IDs independently.
export const deriveRuleChanges = (changes: RuleChange[]): DerivedRuleChange[] => {
  const byRule = new Map<string, RuleChange[]>();
  const keyOf = (c: RuleChange) => `${c.target}:${c.rule_id}`;
  [...changes].sort((a, b) => a.created_at.getTime() - b.created_at.getTime()).forEach((c) => byRule.set(keyOf(c), [...(byRule.get(keyOf(c)) || []), c]));
  const derived: DerivedRuleChange[] = [];
  byRule.forEach((list) => {
    list.forEach((change, i) => {
      const previous = i > 0 ? list[i - 1] : undefined;
      if (change.removed) {
        // A deleted rule has no snapshot; show the last known version.
        const deleted = previous ? { ...change, rule: previous.rule, notification_id: previous.notification_id } : change;
        derived.push({ ...deleted, action: "deleted", summary: [], previous });
        return;
      }
      if (!previous) {
        derived.push({ ...change, action: "created", summary: [], previous });
        return;
      }
      const summary: string[] = [];
      const a = previous.rule;
      const b = change.rule;
      if (a.notification.notificationText !== b.notification.notificationText) summary.push("text changed");
      if (a.notification.severity !== b.notification.severity) summary.push(describe("severity", a.notification.severity, b.notification.severity));
      if ((a.notification.expiresAt?.getTime() || 0) !== (b.notification.expiresAt?.getTime() || 0))
        summary.push(describe("expires_at", a.notification.expiresAt?.toISOString(), b.notification.expiresAt?.toISOString()));
      if (JSON.stringify(ruleScope(a as NotificationRule)) !== JSON.stringify(ruleScope(b as NotificationRule)))
        summary.push(describe("filter", ruleScope(a as NotificationRule), ruleScope(b as NotificationRule)));
      if (a.enabled !== b.enabled) {
        derived.push({ ...change, action: b.enabled ? "enabled" : "disabled", summary, previous });
        return;
      }
      derived.push({ ...change, action: "updated", summary, previous });
    });
  });
  return derived.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
};
